import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { NavItem } from "@/components/layout/constants/navigation";
import { SidebarNavLink } from "@/components/ui/sidebar";
import {
  isNavItemOrChildActive,
  isNavLinkActive,
} from "@/components/layout/utils/isNavLinkActive";
import { ChevronDown, ChevronUp } from "lucide-react";

const LeafNavLink: React.FC<{ item: NavItem; allHrefs: string[] }> = ({
  item,
  allHrefs,
}) => {
  const { pathname } = useLocation();
  const isActive = isNavLinkActive(pathname, item.href, allHrefs);

  return (
    <SidebarNavLink
      as={Link}
      to={item.href}
      active={isActive}
      icon={item.icon}
      iconClassName={item.iconColor}
      total={item.total}
      newCount={item.newCount}
      className="font-medium text-slate-700 [&>svg]:stroke-[3]"
    >
      {item.title}
    </SidebarNavLink>
  );
};

const CollapsibleNavParent: React.FC<{
  item: NavItem;
  allHrefs: string[];
}> = ({ item, allHrefs }) => {
  const { pathname } = useLocation();
  const children = item.items ?? [];
  const childActive = children.some((c) =>
    isNavItemOrChildActive(pathname, c, allHrefs),
  );
  const parentActive = isNavLinkActive(pathname, item.href, allHrefs);
  const sectionActive = parentActive || childActive;

  const [expanded, setExpanded] = useState(() => childActive);

  useEffect(() => {
    if (childActive) {
      setExpanded(true);
    }
  }, [childActive]);

  const toggleExpanded = () => setExpanded((v) => !v);

  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          "flex w-full min-w-0 overflow-hidden rounded-lg border",
          sectionActive &&
            "border-royal-glow-4 bg-royal-glow-5/80 text-accent-foreground",
          !sectionActive &&
            "hover:border-royal-glow-4 hover:bg-royal-glow-4/80 border-transparent",
        )}
      >
        {item.isGroupOnly ? (
          <SidebarNavLink
            as="button"
            type="button"
            active={parentActive}
            icon={item.icon}
            iconClassName={item.iconColor}
            onClick={toggleExpanded}
            className={cn(
              "min-w-0 flex-1 rounded-none border-0 text-left font-medium text-slate-700 shadow-none [&>svg]:stroke-[3]",
              "hover:bg-transparent focus-visible:rounded-none",
            )}
          >
            {item.title}
          </SidebarNavLink>
        ) : (
          <SidebarNavLink
            as={Link}
            to={item.href}
            active={parentActive}
            icon={item.icon}
            iconClassName={item.iconColor}
            className={cn(
              "min-w-0 flex-1 rounded-none border-0 font-medium text-slate-700 shadow-none [&>svg]:stroke-[3]",
              "hover:bg-transparent focus-visible:rounded-none",
            )}
          >
            {item.title}
          </SidebarNavLink>
        )}
        <button
          type="button"
          aria-expanded={expanded}
          aria-label="Toggle submenu"
          onClick={toggleExpanded}
          className={cn(
            "flex shrink-0 items-center justify-center border-0 bg-transparent px-2",
            "text-zinc-400 hover:text-zinc-600",
            "focus-visible:ring-custom-outline/50 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
          )}
        >
          {expanded ? (
            <ChevronUp className="size-4 shrink-0" />
          ) : (
            <ChevronDown className="size-4 shrink-0" />
          )}
        </button>
      </div>
      {expanded && (
        <div className="relative ml-3 flex flex-col gap-1 border-l border-slate-200/80 pl-3">
          {children.map((child) =>
            child.items?.length ? (
              <CollapsibleNavParent
                key={child.href}
                item={child}
                allHrefs={allHrefs}
              />
            ) : (
              <LeafNavLink key={child.href} item={child} allHrefs={allHrefs} />
            ),
          )}
        </div>
      )}
    </div>
  );
};

export const SidebarNavItemRow: React.FC<{
  item: NavItem;
  allHrefs?: string[];
}> = ({ item, allHrefs = [] }) => {
  const hasChildren = Boolean(item.items?.length);
  if (!hasChildren) {
    return <LeafNavLink item={item} allHrefs={allHrefs} />;
  }
  return <CollapsibleNavParent item={item} allHrefs={allHrefs} />;
};
